import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Pencil, Trash, FloppyDisk, X, Toolbox } from "@phosphor-icons/react";

export default function Skills() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: skills = [] } = useQuery({ queryKey: ["skills"], queryFn: () => http.get("/skills").then(r => r.data) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => http.get("/users").then(r => r.data) });

  const techCount = (sid) => users.filter(u => (u.skill_ids || []).includes(sid)).length;

  const canManage = ["admin", "general_manager", "maintenance_manager"].includes(user?.role);
  const canDelete = ["admin", "maintenance_manager"].includes(user?.role);

  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (modal.mode === "create") await http.post("/skills", form);
      else await http.patch(`/skills/${modal.id}`, form);
      qc.invalidateQueries({ queryKey: ["skills"] });
      setModal(null);
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Soft-delete skill "${name}"?`)) return;
    try { await http.delete(`/skills/${id}`); qc.invalidateQueries({ queryKey: ["skills"] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline">Workforce</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1">Skills catalogue</h1>
        </div>
        {canManage && <button onClick={() => { setForm({ name: "", description: "" }); setModal({ mode: "create" }); setErr(""); }} data-testid="skill-new-button"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
          <Plus size={14} weight="bold" /> Add skill
        </button>}
      </div>

      <div className="rmms-card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] font-heading uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="px-4 py-2.5">Skill</th><th className="px-4 py-2.5">Description</th><th className="px-4 py-2.5">Technicians</th><th className="px-4 py-2.5 w-24"></th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {skills.map(s => (
              <tr key={s.id} data-testid={`skill-row-${s.id}`}>
                <td className="px-4 py-3 font-medium flex items-center gap-2"><Toolbox size={12} /> <span className="font-mono-data">{s.name}</span></td>
                <td className="px-4 py-3 text-slate-600">{s.description || "—"}</td>
                <td className="px-4 py-3 font-mono-data">{techCount(s.id)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    {canManage && <button onClick={() => { setForm({ name: s.name, description: s.description || "" }); setModal({ mode: "edit", id: s.id }); setErr(""); }} data-testid={`skill-edit-${s.id}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-50"><Pencil size={12} /></button>}
                    {canDelete && <button onClick={() => remove(s.id, s.name)} data-testid={`skill-delete-${s.id}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-rose-50 hover:text-rose-600"><Trash size={12} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {skills.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-slate-500">No skills yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="skill-modal">
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div><div className="overline">{modal.mode === "create" ? "New" : "Edit"}</div><h2 className="text-xl font-heading font-semibold tracking-tight mt-1">Skill</h2></div>
              <button type="button" onClick={() => setModal(null)} className="h-8 w-8 rounded-sm hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div>
              <label className="overline mb-1.5 block">Name *</label>
              <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="skill-name-input" className="rmms-input font-mono-data" placeholder="e.g. plumber" />
            </div>
            <div>
              <label className="overline mb-1.5 block">Description</label>
              <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="skill-desc-input" className="rmms-input" />
            </div>
            {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1">{err}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <button type="button" onClick={() => setModal(null)} className="h-10 px-4 text-sm border border-slate-200 rounded-md">Cancel</button>
              <button type="submit" disabled={busy} data-testid="skill-submit-button" className="h-10 px-4 rounded-md text-white text-sm inline-flex items-center gap-1.5" style={{ background: "#002FA7" }}>
                <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
